import React, { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { sky } from './skyState';
import { weather } from './weather';
import { useWorldStore } from '../state/worldStore';

// ── THE SKY IS NOT EMPTY ─────────────────────────────────────────────────────
//
// A city's sky has traffic too: an airliner tracking across on its approach,
// nav lights blinking red and green, a contrail by day; and after dark a police
// helicopter working the neighbourhood, its searchlight sweeping the streets
// and lighting a pool wherever it lands. Far enough away to be silhouettes, so
// they are built from primitives — no models, no textures, a handful of draws.
//
// The airliner flies a straight line across the whole sky every minute or two,
// on a fresh heading each pass. The helicopter only flies at night (and not in
// a storm), orbiting a point that drifts around the city.

const RED = new THREE.Color('#ff2a2a');
const GREEN = new THREE.Color('#2aff6a');
const WHITE = new THREE.Color('#ffffff');

function planeGeometry() {
  const parts = [];
  const body = new THREE.CylinderGeometry(0.55, 0.45, 9, 8); body.rotateX(Math.PI / 2); parts.push(body);
  const nose = new THREE.SphereGeometry(0.55, 8, 6); nose.translate(0, 0, 4.5); parts.push(nose);
  const wing = new THREE.BoxGeometry(11, 0.14, 1.8); wing.translate(0, -0.1, 0.4); parts.push(wing);
  const tail = new THREE.BoxGeometry(3.6, 0.12, 1.0); tail.translate(0, 0.1, -4.1); parts.push(tail);
  const fin = new THREE.BoxGeometry(0.12, 1.9, 1.3); fin.translate(0, 1.0, -4.0); parts.push(fin);
  return mergeGeometries(parts.map((g) => g.toNonIndexed()));
}

function heliGeometry() {
  const parts = [];
  const cab = new THREE.SphereGeometry(0.75, 10, 8); cab.scale(1, 0.85, 1.45); parts.push(cab);
  const boom = new THREE.CylinderGeometry(0.12, 0.2, 3.2, 6); boom.rotateX(Math.PI / 2); boom.translate(0, 0.15, -2.3); parts.push(boom);
  const fin = new THREE.BoxGeometry(0.08, 0.8, 0.5); fin.translate(0, 0.45, -3.8); parts.push(fin);
  const skid = new THREE.BoxGeometry(0.08, 0.08, 2.0); skid.translate(0.5, -0.8, 0); parts.push(skid);
  const skid2 = skid.clone(); skid2.translate(-1.0, 0, 0); parts.push(skid2);
  return mergeGeometries(parts.map((g) => g.toNonIndexed()));
}

const BEAM_VERT = /* glsl */`
varying float vH;
varying vec3 vN;
varying vec3 vV;
void main() {
  vec4 wp = modelMatrix * vec4(position, 1.0);
  vH = uv.y;
  vN = normalize(mat3(modelMatrix) * normal);
  vV = normalize(cameraPosition - wp.xyz);
  gl_Position = projectionMatrix * viewMatrix * wp;
}
`;
const BEAM_FRAG = /* glsl */`
uniform float uI;
varying float vH;
varying vec3 vN;
varying vec3 vV;
void main() {
  float edge = pow(abs(dot(normalize(vN), normalize(vV))), 1.4);
  float a = pow(vH, 1.3) * edge * uI;
  gl_FragColor = vec4(vec3(0.92, 0.95, 1.0) * a, 1.0);
}
`;

export default function SkyTraffic() {
  const bounds = useWorldStore((s) => s.cityBounds);
  const geo = useMemo(() => {
    // The beam's pivot is its narrow end (the helicopter), so it can be scaled
    // to reach the street and aimed by a single rotation.
    const beamG = new THREE.CylinderGeometry(0.15, 2.4, 1, 18, 1, true);
    beamG.translate(0, -0.5, 0);
    return { plane: planeGeometry(), heli: heliGeometry(), beam: beamG };
  }, []);
  const mats = useMemo(() => {
    const spot = (() => {
      const c = document.createElement('canvas'); c.width = c.height = 64;
      const g = c.getContext('2d'); const gr = g.createRadialGradient(32, 32, 0, 32, 32, 32);
      gr.addColorStop(0, 'rgba(255,255,255,1)'); gr.addColorStop(0.5, 'rgba(255,255,255,0.45)'); gr.addColorStop(1, 'rgba(255,255,255,0)');
      g.fillStyle = gr; g.fillRect(0, 0, 64, 64);
      const t = new THREE.CanvasTexture(c); return t;
    })();
    return {
      body: new THREE.MeshStandardMaterial({ color: '#c9ced6', roughness: 0.45, metalness: 0.5 }),
      heliBody: new THREE.MeshStandardMaterial({ color: '#1b1f27', roughness: 0.5, metalness: 0.4 }),
      light: new THREE.MeshBasicMaterial({ color: '#ffffff' }),
      trail: new THREE.MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0.0, depthWrite: false }),
      rotor: new THREE.MeshBasicMaterial({ color: '#0d0f13', transparent: true, opacity: 0.35, depthWrite: false, side: THREE.DoubleSide }),
      beam: new THREE.ShaderMaterial({ uniforms: { uI: { value: 0 } }, vertexShader: BEAM_VERT, fragmentShader: BEAM_FRAG, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, fog: false }),
      spot: new THREE.MeshBasicMaterial({ map: spot, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, color: '#dfe8ff', polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -4, fog: false }),
      navR: new THREE.MeshBasicMaterial({ color: RED }),
      navG: new THREE.MeshBasicMaterial({ color: GREEN }),
      strobe: new THREE.MeshBasicMaterial({ color: WHITE }),
    };
  }, []);
  useEffect(() => () => { Object.values(geo).forEach((g) => g.dispose()); Object.values(mats).forEach((m) => { m.map?.dispose(); m.dispose(); }); }, [geo, mats]);

  const plane = useRef(); const heli = useRef(); const rotor = useRef(); const beam = useRef(); const spot = useRef(); const trail = useRef();
  const state = useRef({ t: 1, dur: 60, from: new THREE.Vector3(), to: new THREE.Vector3(), wait: 8, heliA: 0, heliC: new THREE.Vector2(), heliDrift: 0 });
  const tmp = useMemo(() => ({ v: new THREE.Vector3(), d: new THREE.Vector3() }), []);

  useFrame(({ clock }, rawDt) => {
    const dt = Math.min(rawDt, 0.05);
    const s = state.current;
    const t = clock.elapsedTime;
    const night = sky.night;
    // ── THE AIRLINER ────────────────────────────────────────────────────────
    const p = plane.current;
    if (p) {
      if (s.t >= 1) {
        s.wait -= dt;
        p.visible = false;
        if (s.wait <= 0) {
          const a = Math.random() * Math.PI * 2; const R = 520; const alt = 150 + Math.random() * 60;
          s.from.set(Math.cos(a) * R, alt, Math.sin(a) * R);
          s.to.set(-Math.cos(a + 0.4) * R, alt + 15, -Math.sin(a + 0.4) * R);
          s.dur = 55 + Math.random() * 25; s.t = 0; s.wait = 40 + Math.random() * 70;
        }
      } else {
        s.t += dt / s.dur;
        p.visible = true;
        p.position.lerpVectors(s.from, s.to, s.t);
        tmp.d.subVectors(s.to, s.from).normalize();
        p.lookAt(tmp.v.copy(p.position).add(tmp.d));
        const blink = (t % 1.2) < 0.08 ? 1 : 0;
        mats.strobe.color.setScalar(blink * 30 * (0.3 + night));
        mats.navR.color.copy(RED).multiplyScalar(4 + 10 * night);
        mats.navG.color.copy(GREEN).multiplyScalar(4 + 10 * night);
        // A contrail by day, high and thin; gone in rain or above an overcast.
        mats.trail.opacity = 0.32 * (1 - night) * (1 - Math.max(weather.rain, weather.overcast));
      }
    }
    // ── THE HELICOPTER ──────────────────────────────────────────────────────
    const h = heli.current;
    if (h) {
      const on = night > 0.55 && !weather.storm && !!bounds;
      h.visible = on;
      if (beam.current) beam.current.visible = on;
      if (spot.current) spot.current.visible = on;
      if (on) {
        // Orbit a point that wanders around the city.
        s.heliDrift += dt * 0.02;
        const cx = (bounds.min[0] + bounds.max[0]) / 2 + Math.sin(s.heliDrift * 1.3) * (bounds.max[0] - bounds.min[0]) * 0.3;
        const cz = (bounds.min[1] + bounds.max[1]) / 2 + Math.cos(s.heliDrift) * (bounds.max[1] - bounds.min[1]) * 0.25;
        s.heliA += dt * 0.11;
        const r = 26;
        h.position.set(cx + Math.cos(s.heliA) * r, 34 + Math.sin(t * 0.3) * 1.5, cz + Math.sin(s.heliA) * r);
        h.rotation.set(0.08, -s.heliA, 0.12);    // banked into the turn, nose ahead
        if (rotor.current) rotor.current.rotation.y = t * 38;
        // The searchlight: from the belly toward a spot that sweeps the street.
        const sx = cx + Math.cos(s.heliA * 1.7 + 1) * 10; const sz = cz + Math.sin(s.heliA * 1.3) * 8;
        if (beam.current) {
          tmp.v.set(sx, 0, sz);
          tmp.d.subVectors(tmp.v, h.position);
          const len = tmp.d.length();
          beam.current.position.copy(h.position);
          beam.current.scale.set(1, len, 1);
          beam.current.quaternion.setFromUnitVectors(tmp.v.set(0, -1, 0), tmp.d.normalize());
        }
        if (spot.current) spot.current.position.set(sx, 0.06, sz);
        mats.beam.uniforms.uI.value = 0.09 * (0.6 + 0.4 * weather.rain);
        mats.spot.opacity = 0.85;
        mats.light.color.setScalar((t % 1.0) < 0.1 ? 25 : 0);
      }
    }
  });

  return (
    <group>
      <group ref={plane} visible={false} scale={1.1}>
        <mesh geometry={geo.plane} material={mats.body} userData={{ noShadow: true }} />
        <mesh position={[-5.5, -0.05, 0.4]} material={mats.navR}><sphereGeometry args={[0.28, 6, 4]} /></mesh>
        <mesh position={[5.5, -0.05, 0.4]} material={mats.navG}><sphereGeometry args={[0.28, 6, 4]} /></mesh>
        <mesh position={[0, -0.6, 0]} material={mats.strobe}><sphereGeometry args={[0.32, 6, 4]} /></mesh>
        <mesh ref={trail} position={[0, 0, -60]} material={mats.trail} userData={{ noShadow: true }}>
          <boxGeometry args={[0.5, 0.25, 110]} />
        </mesh>
      </group>
      <group ref={heli} visible={false}>
        <mesh geometry={geo.heli} material={mats.heliBody} userData={{ noShadow: true }} />
        <mesh ref={rotor} position={[0, 0.95, 0]} material={mats.rotor} userData={{ noShadow: true }}><circleGeometry args={[2.6, 20]} /></mesh>
        <mesh position={[0, -0.75, 0.6]} material={mats.light}><sphereGeometry args={[0.12, 6, 4]} /></mesh>
      </group>
      {/* The searchlight lives outside the helicopter's group so its own
          rotation (aimed at the street) is not inherited from the bank. */}
      <mesh ref={beam} geometry={geo.beam} material={mats.beam} renderOrder={5} visible={false} userData={{ noShadow: true }} />
      <mesh ref={spot} rotation={[-Math.PI / 2, 0, 0]} material={mats.spot} renderOrder={3} visible={false} userData={{ noShadow: true }}>
        <planeGeometry args={[9, 9]} />
      </mesh>
    </group>
  );
}
